<script setup lang="ts">
import type { Component } from 'vue'

/**
 * One line of a tooltip laid out as icon, label, value. For example
 * "(calendar icon) Created  12 Mar 2026, 14:02".
 *
 * The label is muted and the value is in the normal text colour, so the eye
 * lands on the value. Put several rows in one TooltipContent to list several
 * facts. Pass `icon`, `label` and `value` as props, or use the `icon` and
 * `label` slots and the default slot for anything richer than plain text.
 */
withDefaults(
  defineProps<{
    icon?: Component
    label?: string
    value?: string | number | null
  }>(),
  { icon: undefined, label: undefined, value: undefined },
)
</script>

<template>
  <div data-slot="tooltip-row" class="flex min-w-0 items-baseline gap-2">
    <span
      v-if="icon || $slots.icon"
      class="text-muted-foreground flex size-3.5 shrink-0 items-center justify-center self-center [&_svg]:size-3.5"
      aria-hidden="true"
    >
      <slot name="icon"><component :is="icon" /></slot>
    </span>
    <span v-if="label || $slots.label" class="text-muted-foreground shrink-0">
      <slot name="label">{{ label }}</slot>
    </span>
    <span class="text-foreground min-w-0 font-medium break-words">
      <slot>{{ value }}</slot>
    </span>
  </div>
</template>
